import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createCustomPageRequest,
  getCustomPages,
  removeCustomPage,
  updateCustomPageRequest,
} from "../../../../services/api/landing/customPages/customPagesApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Custom page created successfully!",
    ar: "تم إنشاء الصفحة المخصصة بنجاح!",
  },
  addError: {
    en: "Failed to create custom page.",
    ar: "فشل في إنشاء الصفحة المخصصة.",
  },
  update: {
    en: "Custom page updated successfully!",
    ar: "تم تحديث الصفحة المخصصة بنجاح!",
  },
  updateError: {
    en: "Failed to update custom page.",
    ar: "فشل في تحديث الصفحة المخصصة.",
  },
  delete: {
    en: "Custom page deleted successfully!",
    ar: "تم حذف الصفحة المخصصة بنجاح!",
  },
  deleteError: {
    en: "Failed to delete custom page.",
    ar: "فشل في حذف الصفحة المخصصة.",
  },
};

export const fetchCustomPages = createAsyncThunk(
  "customPages/fetchCustomPages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCustomPages();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch Custom Pages");
    }
  },
);

export const createCustomPage = createAsyncThunk(
  "customPages/createCustomPage",
  async (pageData, { rejectWithValue }) => {
    try {
      const response = await createCustomPageRequest(pageData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create Custom Page");
    }
  },
);

export const updateCustomPage = createAsyncThunk(
  "customPages/updateCustomPage",
  async ({ id, ...pageData }, { rejectWithValue }) => {
    try {
      const response = await updateCustomPageRequest(id, pageData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update Custom Page");
    }
  },
);

export const deleteCustomPage = createAsyncThunk(
  "customPages/deleteCustomPage",
  async (pageId, { rejectWithValue }) => {
    try {
      await removeCustomPage(pageId);
      return pageId;
    } catch (error) {
      return rejectWithValue("Failed to delete Custom Page");
    }
  },
);

const customPagesSlice = createSlice({
  name: "customPages",
  initialState: {
    pagesData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomPages.fulfilled, (state, action) => {
        state.loading = false;
        state.pagesData = action.payload;
      })
      .addCase(fetchCustomPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCustomPage.fulfilled, (state, action) => {
        state.pagesData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createCustomPage.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateCustomPage.fulfilled, (state, action) => {
        const index = state.pagesData.findIndex(
          (page) => page.id === action.payload.id,
        );
        if (index !== -1) {
          state.pagesData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateCustomPage.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteCustomPage.fulfilled, (state, action) => {
        state.pagesData = state.pagesData.filter(
          (page) => page.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteCustomPage.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default customPagesSlice.reducer;
